import React, { useState } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode"; // ✅ fixed import
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const GOOGLE_CLIENT_ID =
  "676635254915-gf554k2eop4g6176t7778k9kge2ucc4v.apps.googleusercontent.com";
const BASE_URL =
  "https://opensheet.elk.sh/11R9T54CjmC2RFZuvmD7fxElItomKuvfTb9t5nHjeWNU";
const USERS_SHEET = `${BASE_URL}/UserAccount`;

function base64Decode(str) {
  try {
    return atob(str);
  } catch {
    return null;
  }
}

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Manual login
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username.trim() || !password) {
      toast.warn("Email and password cannot be empty");
      return;
    }
    if (!isValidEmail(username)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(USERS_SHEET);
      const users = await res.json();

      const user = users.find(
        (u) =>
          (u.Email || "").trim().toLowerCase() === username.trim().toLowerCase()
      );

      if (!user) {
        toast.error("User not found");
        setLoading(false);
        return;
      }

      if ((user.Web || "").trim() !== "1") {
        toast.error("Access denied for web login");
        setLoading(false);
        return;
      }

      const decodedPassword = base64Decode(user.Password || "");
      if (decodedPassword !== password) {
        toast.error("Invalid password");
        setLoading(false);
        return;
      }

      localStorage.setItem(
        "loggedInUser",
        JSON.stringify({
          username: user.Email,
          name:user.Name,
          faculty: user.Faculty || "",
          loginTime: Date.now(),
        })
      );

      toast.success("Login successful! Redirecting...");
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      toast.error("Failed to login. Try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Google login handler
  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      const email = decoded.email;

      if (!email.endsWith("@iimu.ac.in")) {
        toast.error("Please use your iimu.ac.in email address");
        return;
      }

      const res = await fetch(USERS_SHEET);
      const users = await res.json();

      const user = users.find(
        (u) => (u.Email || "").trim().toLowerCase() === email.toLowerCase()
      );

      if (!user) {
        toast.error("This account is not authorized for EMBA LMS access");
        return;
      }

      if ((user.Web || "").trim() !== "1") {
        toast.error("Access denied for web login");
        return;
      }

      localStorage.setItem(
        "loggedInUser",
        JSON.stringify({
          username: user.Email,
          name:user.Name,
          faculty: user.Faculty || "",
          loginTime: Date.now(),
        })
      );

      toast.success("Google login successful! Redirecting...");
      setTimeout(() => navigate("/"), 1500);
    } catch (err) {
      console.error(err);
      toast.error("Google login failed. Try again.");
    }
  };

  const canLogin = isValidEmail(username) && password.length > 0;

  return (
    <>
      <style>{`
        body, html, #root {
          height: 100%;
          margin: 0;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: #fff;
          overflow: hidden;
        }

        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        .login-wrapper {
          display: flex;
          height: 100vh;
          width: 100vw;
          justify-content: center;
          align-items: center;
          gap: 4rem;
          padding: 2rem;
          box-sizing: border-box;
          animation: fadeInUp 0.8s ease forwards;
        }

        .info-panel {
          max-width: 450px;
          line-height: 1.5;
        }

        .info-panel h1 {
          font-size: 2.75rem;
          margin-bottom: 1rem;
          font-weight: 900;
          text-shadow: 1px 1px 6px rgba(0,0,0,0.3);
        }

        .info-panel p {
          font-size: 1.1rem;
          margin-bottom: 2rem;
          color: #d1d5db;
        }

        .benefits-list {
          list-style: none;
          padding-left: 0;
          font-size: 1.05rem;
          color: #e0e7ff;
        }

        .benefits-list li {
          margin-bottom: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.6rem;
        }

        .benefits-list svg {
          width: 20px;
          height: 20px;
          fill: #a5b4fc;
          flex-shrink: 0;
        }

        .login-card {
          background: #ffffffee;
          border-radius: 14px;
          padding: 3rem 3.5rem;
          width: 360px;
          box-shadow: 0 15px 30px rgba(0,0,0,0.2);
          color: #333;
          animation: fadeInUp 0.8s ease forwards;
        }

        .login-header {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 2rem;
          color: #4f46e5;
          text-align: center;
        }

        .form-group {
          position: relative;
          margin-bottom: 2rem;
        }

        .form-control {
          width: 100%;
          border: 2px solid #d1d5db;
          border-radius: 8px;
          font-size: 1rem;
          padding: 1.2rem 1rem 0.6rem 1rem;
          background: #f9fafb;
          color: #333;
          font-weight: 600;
        }

        .btn-login {
          width: 100%;
          background: #4f46e5;
          border: none;
          color: white;
          font-weight: 700;
          font-size: 1.15rem;
          padding: 0.85rem 0;
          border-radius: 10px;
          cursor: pointer;
          box-shadow: 0 8px 25px rgba(79,70,229,0.6);
          transition: background 0.3s ease, box-shadow 0.3s ease;
        }

        .btn-login:disabled {
          background: #a5b4fc;
          cursor: not-allowed;
        }

        .google-login-wrapper {
          margin-top: 1.5rem;
          display: flex;
          justify-content: center;
        }

        @media (max-width: 720px) {
          .login-wrapper { flex-direction: column; padding: 1.5rem; }
          .info-panel { text-align: center; margin-bottom: 2rem; }
          .login-card { width: 100%; max-width: 400px; padding: 2.5rem 2rem; }
          body, html, #root { overflow: scroll !important; }
        }
      `}</style>

      <div className="login-wrapper">
        <section className="info-panel">
          <h1>Welcome to EMBA Learning Management System</h1>
          <p>Your central hub to:</p>
          <ul className="benefits-list">
            <li>
              <svg viewBox="0 0 20 20">
                <path d="M7.629 13.192l-3.854-3.854 1.414-1.414 2.44 2.44 5.94-5.94 1.414 1.414z" />
              </svg>
              View upcoming Zoom meetings seamlessly
            </li>
            <li>
              <svg viewBox="0 0 20 20">
                <path d="M7.629 13.192l-3.854-3.854 1.414-1.414 2.44 2.44 5.94-5.94 1.414 1.414z" />
              </svg>
              Access Zoom recordings anytime
            </li>
            <li>
              <svg viewBox="0 0 20 20">
                <path d="M7.629 13.192l-3.854-3.854 1.414-1.414 2.44 2.44 5.94-5.94 1.414 1.414z" />
              </svg>
              Find all your reading materials in one place
            </li>
          </ul>
          <p>
            Log in now to stay connected and organized throughout your EMBA
            journey.
          </p>
        </section>

        <form onSubmit={handleLogin} className="login-card" noValidate>
          <div className="login-header">Login</div>

          <div className="form-group">
            <input
              id="username"
              type="email"
              className="form-control"
              placeholder=" "
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            <label htmlFor="username">Email</label>
          </div>

          <div className="form-group">
            <input
              id="password"
              type="password"
              className="form-control"
              placeholder=" "
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <label htmlFor="password">Password</label>
          </div>

          <button
            type="submit"
            className="btn-login"
            disabled={!canLogin || loading}
          >
            {loading ? "Logging in..." : "Login"}
          </button>

          {/* ✅ Google Login Button */}
          <div className="google-login-wrapper">
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLogin
                onSuccess={handleGoogleLogin}
                onError={() => toast.error("Google login failed")}
                theme="filled_blue"
                shape="pill"
                width="280"
              />
            </GoogleOAuthProvider>
          </div>
        </form>
      </div>

      <ToastContainer position="top-center" autoClose={2500} />
    </>
  );
};

export default Login;
